import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Smartphone, Mail, Key, Monitor, Clock, AlertTriangle, CheckCircle2, Copy, Eye, EyeOff, Trash2, RefreshCw, Plus, History, ShieldCheck, ShieldAlert, LogOut, Globe, Bell, Settings, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SecurityCentrePage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodesModalOpen, setBackupCodesModalOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  
  // API Keys State
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyModalOpen, setNewKeyModalOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', permissions: ['read'], expires_days: null });
  const [createdKey, setCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  
  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Dashboard State
  const [dashboardData, setDashboardData] = useState(null);
  
  // Alerts State
  const [alerts, setAlerts] = useState([]);
  const [alertsUnreadCount, setAlertsUnreadCount] = useState(0);
  const [alertSettings, setAlertSettings] = useState(null);
  const [alertSettingsModalOpen, setAlertSettingsModalOpen] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };
  
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/alerts`, { headers });
      setAlerts(res.data.alerts || []);
      setAlertsUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  }, []);
  
  const fetchAlertSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/alerts/settings`, { headers });
      setAlertSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch alert settings:', err);
    }
  }, []);

  const fetchTwoFaStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/2fa/status`, { headers });
      setTwoFaStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/sessions`, { headers });
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  }, []);

  const fetchLoginHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/login-history`, { headers });
      setLoginHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch login history:', err);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/api-keys`, { headers });
      setApiKeys(res.data);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/audit-logs?limit=50`, { headers });
      setAuditLogs(res.data);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/security/dashboard`, { headers });
      setDashboardData(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  useEffect(() => {
    fetchTwoFaStatus();
    fetchSessions();
    fetchApiKeys();
    fetchDashboard();
    fetchLoginHistory();
    fetchAlerts();
    fetchAlertSettings();
  }, [fetchTwoFaStatus, fetchSessions, fetchApiKeys, fetchDashboard, fetchLoginHistory, fetchAlerts, fetchAlertSettings]);

  // Alert Functions
  const markAlertRead = async (alertId) => {
    try {
      await axios.put(`${API}/api/security/alerts/${alertId}/read`, {}, { headers });
      fetchAlerts();
    } catch (err) {
      toast.error('Failed to mark alert as read');
    }
  };

  const markAllAlertsRead = async () => {
    try {
      await axios.put(`${API}/api/security/alerts/mark-all-read`, {}, { headers });
      fetchAlerts();
      toast.success('All alerts marked as read');
    } catch (err) {
      toast.error('Failed to mark alerts as read');
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/api/security/alerts/${alertId}`, { headers });
      fetchAlerts();
      toast.success('Alert deleted');
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };

  const clearReadAlerts = async () => {
    try {
      await axios.delete(`${API}/api/security/alerts`, { headers });
      fetchAlerts();
      toast.success('Read alerts cleared');
    } catch (err) {
      toast.error('Failed to clear alerts');
    }
  };

  const updateAlertSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/api/security/alerts/settings`, newSettings, { headers });
      setAlertSettings(newSettings);
      setAlertSettingsModalOpen(false);
      toast.success('Alert settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  // 2FA Functions
  const initiateTotpSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/security/2fa/totp/setup`, {}, { headers });
      setSetupData(res.data);
      setSetupModalOpen(true);
    } catch (err) {
      toast.error('Failed to initiate 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyTotpSetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/security/2fa/totp/verify?code=${verificationCode}`, {}, { headers });
      setBackupCodes(res.data.backup_codes);
      setSetupModalOpen(false);
      setBackupCodesModalOpen(true);
      setVerificationCode('');
      fetchTwoFaStatus();
      toast.success('Two-factor authentication enabled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const disableTotp = async () => {
    const code = prompt('Enter your current 2FA code to disable:');
    if (!code) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/security/2fa/totp/disable?code=${code}`, {}, { headers });
      fetchTwoFaStatus();
      toast.success('Two-factor authentication disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const enableEmail2fa = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/security/2fa/email/enable`, {}, { headers });
      fetchTwoFaStatus();
      toast.success('Email 2FA enabled');
    } catch (err) {
      toast.error('Failed to enable email 2FA');
    } finally {
      setLoading(false);
    }
  };

  const disableEmail2fa = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/security/2fa/email/disable`, {}, { headers });
      fetchTwoFaStatus();
      toast.success('Email 2FA disabled');
    } catch (err) {
      toast.error('Failed to disable email 2FA');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const code = prompt('Enter your current 2FA code:');
    if (!code) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/security/2fa/backup-codes/regenerate?code=${code}`, {}, { headers });
      setBackupCodes(res.data.backup_codes);
      setBackupCodesModalOpen(true);
      toast.success('Backup codes regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate codes');
    } finally {
      setLoading(false);
    }
  };

  // Session Functions
  const revokeSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/api/security/sessions/${sessionId}`, { headers });
      fetchSessions();
      toast.success('Session revoked');
    } catch (err) {
      toast.error('Failed to revoke session');
    }
  };

  const revokeAllSessions = async () => {
    if (!confirm('This will log you out of all other devices. Continue?')) return;
    try {
      await axios.delete(`${API}/api/security/sessions`, { headers });
      fetchSessions();
      toast.success('All other sessions revoked');
    } catch (err) {
      toast.error('Failed to revoke sessions');
    }
  };

  // API Key Functions
  const createApiKey = async () => {
    if (!newKeyData.name) {
      toast.error('Please enter a name for the API key');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/security/api-keys`, newKeyData, { headers });
      setCreatedKey(res.data);
      setNewKeyModalOpen(false);
      fetchApiKeys();
      toast.success('API key created');
    } catch (err) {
      toast.error('Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!confirm('This will permanently revoke this API key. Continue?')) return;
    try {
      await axios.delete(`${API}/api/security/api-keys/${keyId}`, { headers });
      fetchApiKeys();
      toast.success('API key revoked');
    } catch (err) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getSecurityScore = () => {
    if (!twoFaStatus || !dashboardData) return 0;
    let score = 50;
    if (twoFaStatus.totp_enabled) score += 30;
    if (twoFaStatus.email_2fa_enabled) score += 10;
    if (twoFaStatus.has_backup_codes) score += 10;
    return Math.min(score, 100);
  };

  return (
    <div className="space-y-6" data-testid="security-centre-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security Centre
          </h1>
          <p className="text-muted-foreground">Manage your account security settings</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="2fa" data-testid="tab-2fa">Two-Factor Auth</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="api-keys" data-testid="tab-api-keys">API Keys</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts" className="relative">
            Alerts
            {alertsUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                {alertsUnreadCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Security Score Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Security Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className={`text-4xl font-bold ${getSecurityScore() >= 80 ? 'text-green-600' : getSecurityScore() >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {getSecurityScore()}%
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${getSecurityScore() >= 80 ? 'bg-green-500' : getSecurityScore() >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${getSecurityScore()}%` }}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {getSecurityScore() >= 80 ? 'Excellent security!' : getSecurityScore() >= 50 ? 'Good, but can be improved' : 'Enable 2FA to improve'}
                </p>
              </CardContent>
            </Card>

            {/* 2FA Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Two-Factor Authentication</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {twoFaStatus?.totp_enabled ? (
                    <>
                      <ShieldCheck className="h-8 w-8 text-green-500" />
                      <div>
                        <p className="font-medium text-green-600">Enabled</p>
                        <p className="text-xs text-muted-foreground">Authenticator App</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <ShieldAlert className="h-8 w-8 text-amber-500" />
                      <div>
                        <p className="font-medium text-amber-600">Not Enabled</p>
                        <p className="text-xs text-muted-foreground">Enable for better security</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Active Sessions */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Monitor className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{sessions.length}</p>
                    <p className="text-xs text-muted-foreground">Active devices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          {dashboardData && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Failed Logins (24h)</p>
                      <p className="text-2xl font-bold">{dashboardData.stats?.failed_logins_24h || 0}</p>
                    </div>
                    <AlertTriangle className={`h-8 w-8 ${dashboardData.stats?.failed_logins_24h > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active API Keys</p>
                      <p className="text-2xl font-bold">{dashboardData.stats?.active_api_keys || 0}</p>
                    </div>
                    <Key className="h-8 w-8 text-slate-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">High Risk Events</p>
                      <p className="text-2xl font-bold">{dashboardData.stats?.high_risk_events_24h || 0}</p>
                    </div>
                    <AlertTriangle className={`h-8 w-8 ${dashboardData.stats?.high_risk_events_24h > 0 ? 'text-red-500' : 'text-slate-300'}`} />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">2FA Adoption</p>
                      <p className="text-2xl font-bold">{dashboardData.stats?.two_factor_adoption || 0}%</p>
                    </div>
                    <Shield className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Recent Security Events */}
          {dashboardData?.recent_events && dashboardData.recent_events.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Security Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData.recent_events.slice(0, 5).map((event, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          event.risk_level === 'high' || event.risk_level === 'critical' ? 'bg-red-100' : 
                          event.risk_level === 'medium' ? 'bg-amber-100' : 'bg-slate-100'
                        }`}>
                          <History className={`h-4 w-4 ${
                            event.risk_level === 'high' || event.risk_level === 'critical' ? 'text-red-600' :
                            event.risk_level === 'medium' ? 'text-amber-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{event.action.replace(/_/g, ' ').toUpperCase()}</p>
                          <p className="text-xs text-muted-foreground">
                            {event.user?.first_name} {event.user?.last_name} • {event.ip_address}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={event.risk_level === 'high' ? 'destructive' : event.risk_level === 'medium' ? 'secondary' : 'outline'}>
                          {event.risk_level}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(event.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* 2FA Tab */}
        <TabsContent value="2fa" className="space-y-6">
          {/* TOTP Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Smartphone className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Authenticator App (TOTP)</CardTitle>
                  <CardDescription>Use Google Authenticator, Authy, or similar apps</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {twoFaStatus?.totp_enabled ? (
                    <Badge className="bg-green-100 text-green-700">Enabled</Badge>
                  ) : (
                    <Badge variant="secondary">Not Enabled</Badge>
                  )}
                  {twoFaStatus?.totp_verified && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                </div>
                <div className="flex gap-2">
                  {twoFaStatus?.totp_enabled ? (
                    <>
                      <Button variant="outline" size="sm" onClick={regenerateBackupCodes}>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        New Backup Codes
                      </Button>
                      <Button variant="destructive" size="sm" onClick={disableTotp}>
                        Disable
                      </Button>
                    </>
                  ) : (
                    <Button onClick={initiateTotpSetup} disabled={loading} data-testid="enable-totp-btn">
                      <Shield className="h-4 w-4 mr-1" />
                      Enable TOTP
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email 2FA Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Mail className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>Email Verification</CardTitle>
                  <CardDescription>Receive verification codes via email</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {twoFaStatus?.email_2fa_enabled ? (
                    <Badge className="bg-green-100 text-green-700">Enabled</Badge>
                  ) : (
                    <Badge variant="secondary">Not Enabled</Badge>
                  )}
                </div>
                <Switch 
                  checked={twoFaStatus?.email_2fa_enabled || false}
                  onCheckedChange={(checked) => checked ? enableEmail2fa() : disableEmail2fa()}
                  data-testid="email-2fa-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Backup Codes Info */}
          {twoFaStatus?.has_backup_codes && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                You have backup codes saved. These can be used if you lose access to your authenticator app.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Active Sessions</CardTitle>
                <CardDescription>Manage devices logged into your account</CardDescription>
              </div>
              <Button variant="outline" onClick={revokeAllSessions} data-testid="revoke-all-sessions-btn">
                <LogOut className="h-4 w-4 mr-1" />
                Sign Out All Others
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sessions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No active sessions found</p>
                ) : (
                  sessions.map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Monitor className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {session.device_info || session.user_agent?.split(' ')[0] || 'Unknown Device'}
                            {session.is_current && <Badge className="bg-green-100 text-green-700 text-xs">Current</Badge>}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Globe className="h-3 w-3" /> {session.ip_address || 'Unknown IP'}
                            <Clock className="h-3 w-3 ml-2" /> Last active: {formatDate(session.last_active_at)}
                          </p>
                        </div>
                      </div>
                      {!session.is_current && (
                        <Button variant="ghost" size="sm" onClick={() => revokeSession(session.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Login History */}
          <Card>
            <CardHeader>
              <CardTitle>Login History</CardTitle>
              <CardDescription>Recent login attempts to your account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {loginHistory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No login history</p>
                ) : (
                  loginHistory.slice(0, 10).map((login) => (
                    <div key={login.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {login.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">
                            {login.success ? 'Successful Login' : 'Failed Attempt'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {login.ip_address} • {login.device_info || 'Unknown device'}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDate(login.timestamp)}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>Manage API keys for integrations</CardDescription>
              </div>
              <Button onClick={() => setNewKeyModalOpen(true)} data-testid="create-api-key-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create API Key
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8">
                    <Key className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-muted-foreground">No API keys created yet</p>
                    <p className="text-sm text-muted-foreground">API keys allow external applications to access your account</p>
                  </div>
                ) : (
                  apiKeys.map((key) => (
                    <div key={key.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Key className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {key.name}
                            {key.is_active ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground font-mono">
                            {key.key_prefix}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Permissions: {key.permissions?.join(', ')} • 
                            Created: {formatDate(key.created_at)} • 
                            Last used: {formatDate(key.last_used_at)}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => revokeApiKey(key.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              API keys provide full access to your account. Keep them secure and never share them publicly.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BellRing className="h-5 w-5" />
                  Security Alerts
                </CardTitle>
                <CardDescription>Real-time notifications about security events</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAlertSettingsModalOpen(true)}>
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </Button>
                {alertsUnreadCount > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllAlertsRead}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Mark All Read
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={clearReadAlerts}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear Read
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                    <p className="text-muted-foreground">No security alerts</p>
                    <p className="text-sm text-muted-foreground">You'll be notified of suspicious activity here</p>
                  </div>
                ) : (
                  alerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className={`flex items-start justify-between p-4 border rounded-lg ${alert.status === 'unread' ? 'bg-blue-50 border-blue-200' : ''}`}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-full ${
                          alert.severity === 'critical' ? 'bg-red-100' :
                          alert.severity === 'high' ? 'bg-orange-100' :
                          alert.severity === 'medium' ? 'bg-amber-100' :
                          alert.severity === 'low' ? 'bg-blue-100' : 'bg-slate-100'
                        }`}>
                          <AlertTriangle className={`h-4 w-4 ${
                            alert.severity === 'critical' ? 'text-red-600' :
                            alert.severity === 'high' ? 'text-orange-600' :
                            alert.severity === 'medium' ? 'text-amber-600' :
                            alert.severity === 'low' ? 'text-blue-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm">{alert.title}</p>
                            <Badge variant={
                              alert.severity === 'critical' ? 'destructive' :
                              alert.severity === 'high' ? 'destructive' :
                              alert.severity === 'medium' ? 'secondary' : 'outline'
                            } className="text-xs">
                              {alert.severity}
                            </Badge>
                            {alert.status === 'unread' && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">New</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{alert.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(alert.created_at)} {alert.ip_address && `• IP: ${alert.ip_address}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {alert.status === 'unread' && (
                          <Button variant="ghost" size="sm" onClick={() => markAlertRead(alert.id)}>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteAlert(alert.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Alert Types Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alert Types</CardTitle>
              <CardDescription>Types of security events that trigger alerts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>Multiple failed logins</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span>New device login</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span>New location login</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span>Password changed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span>2FA disabled</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-500" />
                  <span>API key activity</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Security Audit Log</CardTitle>
                <CardDescription>Track all security-related activities</CardDescription>
              </div>
              <Button variant="outline" onClick={fetchAuditLogs}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {auditLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No audit logs found</p>
                ) : (
                  auditLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant={
                          log.risk_level === 'critical' ? 'destructive' :
                          log.risk_level === 'high' ? 'destructive' :
                          log.risk_level === 'medium' ? 'secondary' : 'outline'
                        }>
                          {log.risk_level}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{log.action.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">
                            {log.user?.first_name} {log.user?.last_name} • {log.ip_address}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* TOTP Setup Modal */}
      <Dialog open={setupModalOpen} onOpenChange={setSetupModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Authenticator App</DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={setupData.qr_code} alt="QR Code" className="rounded-lg border" />
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Can't scan? Enter this code manually:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="bg-slate-100 px-3 py-1 rounded font-mono text-sm">{setupData.secret}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(setupData.secret)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app</Label>
                <Input
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg font-mono"
                  data-testid="totp-verification-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupModalOpen(false)}>Cancel</Button>
            <Button onClick={verifyTotpSetup} disabled={loading || verificationCode.length !== 6} data-testid="verify-totp-btn">
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Modal */}
      <Dialog open={backupCodesModalOpen} onOpenChange={setBackupCodesModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
            <DialogDescription>
              Store these codes in a safe place. You can use them to access your account if you lose your authenticator.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 my-4">
            {backupCodes.map((code, idx) => (
              <code key={idx} className="bg-slate-100 px-3 py-2 rounded text-center font-mono">{code}</code>
            ))}
          </div>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Each code can only be used once. Store them securely!
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => copyToClipboard(backupCodes.join('\n'))}>
              <Copy className="h-4 w-4 mr-1" />
              Copy All
            </Button>
            <Button onClick={() => setBackupCodesModalOpen(false)}>I've Saved Them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New API Key Modal */}
      <Dialog open={newKeyModalOpen} onOpenChange={setNewKeyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New API Key</DialogTitle>
            <DialogDescription>
              API keys allow external applications to access your account programmatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Key Name</Label>
              <Input
                placeholder="e.g., My Integration"
                value={newKeyData.name}
                onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                data-testid="api-key-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <Select
                value={newKeyData.permissions[0]}
                onValueChange={(v) => setNewKeyData({ ...newKeyData, permissions: [v] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Read Only</SelectItem>
                  <SelectItem value="write">Read & Write</SelectItem>
                  <SelectItem value="admin">Full Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expiration</Label>
              <Select
                value={newKeyData.expires_days?.toString() || 'never'}
                onValueChange={(v) => setNewKeyData({ ...newKeyData, expires_days: v === 'never' ? null : parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewKeyModalOpen(false)}>Cancel</Button>
            <Button onClick={createApiKey} disabled={loading || !newKeyData.name} data-testid="confirm-create-key-btn">
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created Key Display Modal */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Copy this key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          {createdKey && (
            <div className="space-y-4">
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={createdKey.key}
                  readOnly
                  className="font-mono pr-20"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setShowKey(!showKey)}>
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdKey.key)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {createdKey.warning}
                </AlertDescription>
              </Alert>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setCreatedKey(null); setShowKey(false); }}>
              I've Copied the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityCentrePage;
